import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useRouletteSystem } from '@/hooks/useRouletteSystem'
import { Coins, TrendingUp, TrendingDown, RotateCcw, Timer, Award } from 'lucide-react'

interface RouletteWheelProps {
  currentXP: number
}

const RouletteWheel = ({ currentXP }: RouletteWheelProps) => {
  const [spinning, setSpinning] = useState(false)
  const [canSpin, setCanSpin] = useState(false)
  const [dailyStatus, setDailyStatus] = useState<any>(null)
  const [history, setHistory] = useState<any>(null)
  
  const { loading, error, spinRoulette, getDailyStatus, getHistory } = useRouletteSystem()

  useEffect(() => {
    loadDailyStatus()
    loadHistory()
  }, [])

  const loadDailyStatus = async () => {
    const status = await getDailyStatus()
    if (status) {
      setDailyStatus(status)
      setCanSpin(status.can_spin)
    }
  }

  const loadHistory = async () => {
    const historyData = await getHistory()
    if (historyData) {
      setHistory(historyData)
    }
  }

  const handleSpin = async () => {
    if (!canSpin || loading || spinning) return

    setSpinning(true)
    
    try {
      const result = await spinRoulette()
      if (result) {
        setCanSpin(false)
        await loadDailyStatus()
        await loadHistory()
      }
    } catch (err) {
      console.error('Spin failed:', err)
    } finally {
      // Keep spinning animation for visual effect
      setTimeout(() => setSpinning(false), 2000)
    }
  }

  const getResultIcon = (result: string) => {
    switch (result) {
      case 'big_win':
      case 'medium_win':
      case 'small_win':
        return <TrendingUp className="h-4 w-4 text-green-400" />
      case 'small_loss':
      case 'medium_loss':
      case 'big_loss':
        return <TrendingDown className="h-4 w-4 text-red-400" />
      default:
        return <Coins className="h-4 w-4 text-foreground" />
    }
  }

  const getResultColor = (result: string) => {
    if (result.includes('win')) return 'text-green-400'
    if (result.includes('loss')) return 'text-red-400'
    return 'text-muted-foreground'
  }

  const formatResult = (result: string) => {
    return result.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  const getTimeUntilNextSpin = () => {
    if (!dailyStatus?.next_spin_at) return null
    
    const nextSpin = new Date(dailyStatus.next_spin_at)
    const now = new Date()
    const diff = nextSpin.getTime() - now.getTime()
    
    if (diff <= 0) return 'Available now!'
    
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    return `${hours}h ${minutes}m`
  }

  return (
    <div className="container mx-auto p-6 bg-background min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 text-foreground">Daily Roulette</h1>
        <p className="text-muted-foreground">
          Spin the wheel of fortune once per day to win or lose BroScore!
        </p>
      </div>

      <Tabs defaultValue="wheel" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 bg-secondary border-border">
          <TabsTrigger value="wheel" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Roulette Wheel
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            History & Stats
          </TabsTrigger>
        </TabsList>

        <TabsContent value="wheel" className="space-y-6">
          {/* Current Status */}
          <Card className="border-border/20 bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Coins className="h-5 w-5" />
                Current Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Current BroScore</p>
                  <p className="text-2xl font-bold text-foreground">{currentXP}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Daily Spin</p>
                  <Badge 
                    className={canSpin ? 
                      "bg-primary text-primary-foreground" : 
                      "bg-secondary text-secondary-foreground border-border"
                    }
                  >
                    {canSpin ? "Available" : "Used"}
                  </Badge>
                </div>
              </div>

              {!canSpin && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Timer className="h-4 w-4" />
                  Next spin in: {getTimeUntilNextSpin()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Today's Result */}
          {dailyStatus?.today_result && (
            <Card className="border-border/20 bg-card">
              <CardHeader>
                <CardTitle className="text-foreground">Today's Result</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  {getResultIcon(dailyStatus.today_result.result)}
                  <div>
                    <p className="font-semibold text-foreground">
                      {formatResult(dailyStatus.today_result.result)}
                    </p>
                    <p className={`text-sm ${getResultColor(dailyStatus.today_result.result)}`}>
                      {dailyStatus.today_result.xp_change > 0 ? '+' : ''}
                      {dailyStatus.today_result.xp_change} BroScore
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Roulette Wheel */}
          <Card className="border-border/20 bg-card">
            <CardHeader>
              <CardTitle className="text-foreground">Spin the Wheel</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="mb-6">
                <div 
                  className={`mx-auto w-32 h-32 rounded-full border-8 border-primary flex items-center justify-center text-6xl transition-transform duration-2000 ${
                    spinning ? 'animate-spin' : ''
                  }`}
                  style={{
                    background: 'conic-gradient(from 0deg, #ef4444 0deg 60deg, #f59e0b 60deg 120deg, #10b981 120deg 180deg, #ef4444 180deg 240deg, #f59e0b 240deg 300deg, #10b981 300deg 360deg)'
                  }}
                >
                  <RotateCcw className="h-12 w-12 text-white drop-shadow-lg" />
                </div>
              </div>

              <Button 
                onClick={handleSpin}
                disabled={!canSpin || loading || spinning}
                size="lg"
                className="px-8"
              >
                {spinning ? 'Spinning...' : canSpin ? 'Spin Roulette' : 'Come Back Tomorrow'}
              </Button>

              <div className="mt-6 p-4 bg-muted rounded-lg border border-border/20">
                <h4 className="text-foreground font-semibold mb-3">Prize Distribution</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div className="flex justify-between">
                    <span>🎯 Big Win: +100 BroScore</span>
                    <span className="text-green-400">5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>🎉 Medium Win: +50 BroScore</span>
                    <span className="text-green-400">15%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>✨ Small Win: +25 BroScore</span>
                    <span className="text-green-400">25%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>😐 Small Loss: -10 BroScore</span>
                    <span className="text-red-400">30%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>😞 Medium Loss: -25 BroScore</span>
                    <span className="text-red-400">20%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>💥 Big Loss: -50 BroScore</span>
                    <span className="text-red-400">5%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          {/* Stats Overview */}
          {history && (
            <Card className="border-border/20 bg-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-foreground">
                  <Award className="h-5 w-5" />
                  Statistics (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-foreground">{history.stats.total_spins}</p>
                    <p className="text-sm text-muted-foreground">Total Spins</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${history.stats.total_xp_gained >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {history.stats.total_xp_gained > 0 ? '+' : ''}
                      {history.stats.total_xp_gained}
                    </p>
                    <p className="text-sm text-muted-foreground">Net XP</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">{history.stats.win_count}</p>
                    <p className="text-sm text-muted-foreground">Wins</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-foreground">{history.stats.win_rate}%</p>
                    <p className="text-sm text-muted-foreground">Win Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent History */}
          <Card className="border-border/20 bg-card">
            <CardHeader>
              <CardTitle className="text-foreground">Recent Spins</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history?.history?.length === 0 ? (
                  <div className="text-center py-8">
                    <Coins className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No spins yet</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Come back daily to spin the roulette!
                    </p>
                  </div>
                ) : (
                  history?.history?.map((spin: any) => (
                    <div 
                      key={spin.id} 
                      className="flex items-center justify-between p-3 border border-border/20 rounded-lg bg-muted"
                    >
                      <div className="flex items-center gap-3">
                        {getResultIcon(spin.result)}
                        <div>
                          <p className="font-medium text-foreground">{formatResult(spin.result)}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(spin.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className={`font-semibold ${getResultColor(spin.result)}`}>
                        {spin.xp_change > 0 ? '+' : ''}{spin.xp_change}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white p-4 rounded-lg max-w-md">
          <p className="font-semibold">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}
    </div>
  )
}

export default RouletteWheel
